import { useEffect, useMemo, useRef, useState } from 'react';
import { EsrsReportDocsGenerator } from './DocGenerator';
import { ReportStandardPage } from './ReportStandardPage';
import { useESRSReportStructure, useReportAnswers } from './Report.hooks';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCurrentCompany } from 'utils/hooks';
import { useFeatureFlags } from 'containers/Navigation';
import { useEsrsAssessmentYearQuery, useGetAssessmentCurrencyConversionQuery } from 'models';
import { useReportProgress } from './ReportProgress.hooks';
import ReportContainer from './ReportUIComponents/ReportContainer';
import ReportSideMenu from './ReportUIComponents/ReportSideMenu';

enum ReportViews {
  prepare = 'Prepare',
  preview = 'Preview',
}

export const CreateReport = () => {
  const { esrsAssessmentId = '' } = useParams();
  const [selectedView, setSelectedView] = useState<string>(ReportViews.prepare);
  const {
    structure,
    standards,
    groupMaterialMetricsRefs,
    loading: structureLoading,
  } = useESRSReportStructure(esrsAssessmentId);
  const [searchParams] = useSearchParams();
  const urlStandardRef = useMemo(() => searchParams.get('standardRef'), [searchParams]);
  const urlStdIndex = useMemo(
    () => standards.findIndex((std) => std.reference === urlStandardRef),
    [urlStandardRef, standards]
  );
  const [standardIndex, setStandardIndex] = useState(0);
  const [selectedDr, setSelectedDr] = useState('');
  const reportRef = useRef<HTMLDivElement>(null);

  const { company } = useCurrentCompany();
  const { canDownloadEsrsReport } = useFeatureFlags({ company });

  useEffect(() => setSelectedDr(''), [standardIndex]);

  const { reportAnswers, loading: loadingReportAnswers } = useReportAnswers(esrsAssessmentId);

  const { data: currencyConversionData } = useGetAssessmentCurrencyConversionQuery({
    fetchPolicy: 'network-only',
    variables: {
      assessmentId: esrsAssessmentId,
    },
    skip: !company?.isGroupOwner,
  });

  const { data: assessmentYear } = useEsrsAssessmentYearQuery({
    fetchPolicy: 'network-only',
    variables: {
      esrsAssessmentId,
    },
    skip: !esrsAssessmentId,
  });

  useEffect(() => {
    if (urlStdIndex >= 0) {
      setStandardIndex(urlStdIndex);
    }
  }, [urlStdIndex]);

  const progress = useReportProgress(esrsAssessmentId);

  return (
    <ReportContainer
      answersLoading={loadingReportAnswers}
      canDownloadEsrsReport={canDownloadEsrsReport}
      reportDocGenerator={<EsrsReportDocsGenerator />}
      standards={standards}
      structureLoading={structureLoading}
      selectedView={selectedView}
      setSelectedView={setSelectedView}
      standardIndex={standardIndex}
      setStandardIndex={setStandardIndex}
      sideMenu={
        <ReportSideMenu
          structure={structure}
          selectedStandard={standards?.[standardIndex]?.title}
          selectedStandardDRs={standards?.[standardIndex]?.disclosureRequirementRefs}
          isPreview={selectedView !== ReportViews.prepare}
          selectedDr={selectedDr}
          isLoading={structureLoading}
          setSelectedDr={setSelectedDr}
          reportRef={reportRef}
          setSelectedStandard={(val) =>
            setStandardIndex(standards.findIndex((std) => std.title === val))
          }
          progress={progress}
        />
      }
      reportContent={
        <ReportStandardPage
          standardIndex={standardIndex}
          setStandardIndex={setStandardIndex}
          reportRef={reportRef}
          standards={standards}
          reportAnswers={reportAnswers}
          isPreview={selectedView !== ReportViews.prepare}
          currencyConversionData={currencyConversionData?.esrs_AssessmentCurrencyConversion}
          groupMetricsRefs={groupMaterialMetricsRefs}
          structure={structure}
          year={assessmentYear?.esrsAssessment?.reportingYear}
        />
      }
    />
  );
};
